/**
 * UPLOADING AN FIC NOTICE, AND READING THE NAMES OFF IT.
 *
 *   "Because sanctioned persons arrive from the FIC in pdf format, I want to be
 *    able to upload that pdf, and have that part of the portal extract the
 *    named individuals."
 *
 * ---------------------------------------------------------------------------
 * WHAT WAS ACTUALLY MISSING WAS NOT THE PDF
 *
 * Measured on a database built from every migration: `sanctions_list_entries`
 * held ONE row, and no function anywhere could add another. Screening has run
 * against that list on every signup since script 29 — an FIC Act s26A
 * obligation checking every member against an empty list and finding nothing,
 * which is indistinguishable from working. Script 304 is the writer; this is
 * the way to it.
 *
 * ---------------------------------------------------------------------------
 * THE EXTRACTION PROPOSES. THE PERSON DECIDES. THE DATABASE RECORDS.
 *
 * A false name on this list holds a real person's account, and a PDF parse is a
 * guess about where the words sit on a page. So nothing is written by reading
 * the document: every proposed name is shown, every one can be edited or
 * unticked, and a name the extraction missed can be typed in. The button that
 * writes says how many names it is about to write.
 *
 * THE PDF NEVER LEAVES THE BROWSER. It is read here; what crosses the wire is
 * the list somebody ticked. That is not a nicety — it is a document naming
 * sanctioned individuals, and the less of it that travels the better.
 *
 * ---------------------------------------------------------------------------
 * EVERYTHING THIS SCREEN SAYS ABOUT THE RULES, THE DATABASE ENFORCES
 *
 * The notice reference, the captured reason, the batch ceiling, the refusal of
 * a line that is not a name — all of them live in
 * `fn_add_sanctions_list_entries` and none of them is re-checked here. The
 * screen states them before somebody types rather than after, because being
 * refused by Postgres with the work you just did thrown away is its own small
 * cruelty. The same reasoning as the verdict form beside it.
 */

import { useCallback, useRef, useState } from 'react';
import {
  O, SANS, Card, Field, Notice, PrimaryCTA, QuietButton, StatusPill,
  textInputStyle,
} from '../ui';
import { addSanctionsListEntries, MAX_NAMES_PER_UPLOAD } from '../lib/sanctions';
import {
  entriesFromText, namesFromText, noticeReferenceFromText, readPdfText,
} from '../lib/sanctionsPdf';

/** The database's floor for a captured reason, stated before somebody types. */
const MIN_REASON = 20;

const rowStyle = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '9px 0', borderBottom: `1px solid ${O.hairline}`,
};

export default function SanctionsUpload({ onWritten }) {
  const [reading, setReading] = useState(false);
  const [problem, setProblem] = useState(null);
  const [names, setNames] = useState(null);      // null = nothing read yet
  const [notice, setNotice] = useState('');
  const [reason, setReason] = useState('');
  const [added, setAdded] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const read = useCallback(async (file) => {
    if (!file) return;
    setReading(true);
    setProblem(null);
    setResult(null);
    try {
      const bytes = await file.arrayBuffer();
      const { text, hasTextLayer, pages } = await readPdfText(bytes);

      // A SCANNED NOTICE AND A NOTICE WITH NO NAMES ARE DIFFERENT PROBLEMS, and
      // told apart because "no names found" on a scan sends somebody looking
      // for a bug in the tool rather than for a text-based copy of the notice.
      if (!hasTextLayer) {
        setNames([]);
        setProblem(
          `That PDF has no text in it — ${pages} page${pages === 1 ? '' : 's'} of image. `
          + 'It is most likely a scan. Ask the FIC for the text version, or type the names in below.');
        return;
      }

      // THE STRUCTURED READER FIRST, AND THE HEURISTIC ONLY IF IT FINDS
      // NOTHING.
      //
      // Measured on the founder's TFS-922: the heuristic proposed two section
      // headings and missed the only listed person, because a real notice is a
      // labelled record rather than prose with names in it. The structured
      // reader also carries the UN reference, the date of birth and the
      // nationality, which is the difference between holding the right account
      // and holding somebody else's.
      //
      // The heuristic stays as the fallback because ONE notice is one notice.
      // A differently-shaped one that this reader cannot see should still put
      // something in front of a compliance officer to correct.
      const structured = entriesFromText(text);
      const found = structured.length > 0
        ? structured
        : namesFromText(text).map((n) => ({ fullName: n }));
      setNames(found.map((e) => ({
        name: e.fullName,
        keep: true,
        reference: e.reference || null,
        dateOfBirth: e.dateOfBirth || null,
        nationality: e.nationality || null,
        designation: e.designation || null,
      })));
      if (!notice) setNotice(noticeReferenceFromText(text));
      if (found.length === 0) {
        setProblem(
          'That PDF has text in it, but nothing in it looked like a person’s name. '
          + 'Check it is the notice itself and not a covering letter, or type the names in below.');
      }
    } catch (e) {
      setNames([]);
      setProblem(`That file could not be read as a PDF: ${e.message || e}`);
    } finally {
      setReading(false);
    }
  }, [notice]);

  const kept = (names || []).filter((n) => n.keep && n.name.trim());
  const tooMany = kept.length > MAX_NAMES_PER_UPLOAD;
  const ready = kept.length > 0
    && notice.trim().length > 0
    && reason.trim().length >= MIN_REASON
    && !tooMany;

  const write = async () => {
    setBusy(true);
    setProblem(null);
    const res = await addSanctionsListEntries({
      // EVERYTHING THE NOTICE SAID, not just the name. A sanctions entry with
      // a date of birth on it is one a screener can tell from a namesake.
      entries: kept.map((n) => ({
        fullName: n.name.trim(),
        reference: n.reference || null,
        dateOfBirth: n.dateOfBirth || null,
        nationality: n.nationality || null,
        designation: n.designation || null,
      })),
      sourceNotice: notice.trim(),
      reason: reason.trim(),
    });
    setBusy(false);
    if (res.error) { setProblem(res.error); return; }
    setResult(res.data);
    setNames(null);
    setReason('');
    if (fileRef.current) fileRef.current.value = '';
    if (onWritten) onWritten();
  };

  return (
    <Card style={{ marginBottom: 18 }}>
      <h3 style={{ fontFamily: SANS, fontSize: 14.5, fontWeight: 700, color: O.ink, margin: '0 0 6px' }}>
        Add names from an FIC notice
      </h3>
      <p style={{
        fontFamily: SANS, fontSize: 12.5, color: O.inkMuted,
        margin: '0 0 14px', lineHeight: 1.7, maxWidth: 620,
      }}>
        The notice is read here in your browser and is not uploaded anywhere. Orchid
        proposes the names it finds; you decide which of them go onto the list.
        <strong style={{ color: O.ink }}> Check every one.</strong> A name added here is
        screened against every person who signs up, and a wrong one holds a real
        account.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,.pdf"
        aria-label="Choose the FIC notice PDF"
        onChange={(e) => read(e.target.files && e.target.files[0])}
        style={{ fontFamily: SANS, fontSize: 12.5, marginBottom: 14 }}
      />

      {reading && <Notice tone="info">Reading the notice…</Notice>}
      {problem && <Notice tone="critical">{problem}</Notice>}

      {result && (
        <Notice tone="info">
          {result.length} name{result.length === 1 ? '' : 's'} handled:{' '}
          {['added', 'amended', 'already listed', 'not a name']
            .map((k) => [k, result.filter((r) => r.outcome === k).length])
            .filter(([, n]) => n > 0)
            .map(([k, n]) => `${n} ${k}`)
            .join(', ')}.
        </Notice>
      )}

      {names && (
        <>
          <div style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            gap: 10, marginTop: 6, marginBottom: 4,
          }}>
            <span style={{ fontFamily: SANS, fontSize: 12.5, fontWeight: 600, color: O.ink }}>
              {names.length === 0
                ? 'No names proposed'
                : `${names.length} name${names.length === 1 ? '' : 's'} proposed`}
            </span>
            <StatusPill tone="quiet">{kept.length} selected</StatusPill>
          </div>

          {names.map((n, i) => (
            // The index is the key deliberately: these rows are editable text
            // and two people can legitimately share a name on one notice.
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} style={rowStyle}>
              <input
                type="checkbox"
                checked={n.keep}
                aria-label={`Add ${n.name} to the sanctions list`}
                onChange={(e) => setNames(names.map((m, j) =>
                  (j === i ? { ...m, keep: e.target.checked } : m)))}
              />
              <input
                type="text"
                value={n.name}
                aria-label={`Name ${i + 1}, as it will be listed`}
                onChange={(e) => setNames(names.map((m, j) =>
                  (j === i ? { ...m, name: e.target.value } : m)))}
                style={{ ...textInputStyle(false), flex: 1, minWidth: 0 }}
              />
            </div>
          ))}

          {/* THE FIELD THAT EXISTS BECAUSE THE EXTRACTION IS TUNED TO MISS.
              It leans towards proposing fewer lines, on the grounds that
              inventing a name costs somebody their account and missing one
              costs a second look — so there has to be a way to add the one it
              missed without leaving the screen. */}
          <div style={{ display: 'flex', gap: 9, marginTop: 12 }}>
            <input
              type="text"
              value={added}
              placeholder="A name the extraction missed"
              aria-label="Add a name the extraction missed"
              onChange={(e) => setAdded(e.target.value)}
              style={{ ...textInputStyle(false), flex: 1, minWidth: 0 }}
            />
            <QuietButton
              onClick={() => {
                if (!added.trim()) return;
                setNames([...names, { name: added.trim(), keep: true }]);
                setAdded('');
              }}
            >Add</QuietButton>
          </div>

          <div style={{ marginTop: 16 }}>
            <Field label="The FIC notice this came from" htmlFor="fic-notice">
              <input
                id="fic-notice"
                type="text"
                value={notice}
                onChange={(e) => setNotice(e.target.value)}
                placeholder="FIC/TFS/2026-11"
                style={textInputStyle(false)}
              />
            </Field>
          </div>

          <div style={{ marginTop: 12 }}>
            <Field label="Why these names are being added" htmlFor="fic-reason">
              <input
                id="fic-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="At least 20 characters — this goes on the compliance log"
                style={textInputStyle(false)}
              />
            </Field>
          </div>

          {tooMany && (
            <Notice tone="critical">
              {kept.length} names is more than one person can have checked. The database
              refuses a batch over {MAX_NAMES_PER_UPLOAD}. Either the notice needs
              splitting, or the extraction has found lines rather than people.
            </Notice>
          )}

          <div style={{ marginTop: 14 }}>
            <PrimaryCTA onClick={write} disabled={!ready || busy} style={{ width: '100%' }}>
              {busy
                ? 'Adding…'
                : `Add ${kept.length} name${kept.length === 1 ? '' : 's'} to the sanctions list`}
            </PrimaryCTA>
          </div>
        </>
      )}
    </Card>
  );
}
